import { useEffect, useMemo } from "react";
import clinicAPI from "../../../services/clinicApi";
import s from "../clinic.module.css";
import { useToast } from "../../ui";
import {
  CALENDAR_HOURS,
  sessionColor,
  sessionDurationRows,
  sessionLabel,
  sessionsStartingInHour,
} from "./calendarUtils";
import { useSessionGridDrag } from "./sessionDragDrop";
import DaySessionPanel from "./DaySessionPanel";
import SessionCalendarCard from "./SessionCalendarCard";

export default function DayScheduleView({
  day,
  sessions,
  groups,
  branches = [],
  doctors = [],
  selectedId,
  onSelect,
  onUpdated,
  detailMode = "aside",
  onOpenDetail,
  appCompact = false,
}) {
  const toast = useToast();

  const daySessions = useMemo(
    () =>
      [...sessions]
        .filter((sess) => sess.start_datetime?.slice(0, 10) === day)
        .sort(
          (a, b) => new Date(a.start_datetime) - new Date(b.start_datetime),
        ),
    [sessions, day],
  );

  const selectedPreview =
    daySessions.find((sess) => sess.id === selectedId) || null;

  const pickSession = (id) => {
    onSelect(id);
    if (detailMode === "sheet" && id && onOpenDetail) {
      onOpenDetail(id);
    }
  };

  useEffect(() => {
    if (detailMode === "sheet") return;
    if (!daySessions.length) {
      if (selectedId) onSelect(null);
      return;
    }
    if (!selectedId || !daySessions.some((sess) => sess.id === selectedId)) {
      onSelect(daySessions[0].id);
    }
  }, [daySessions, selectedId, onSelect, detailMode]);

  const dnd = useSessionGridDrag({
    sessions: daySessions,
    onReschedule: async (sess, patch) => {
      await clinicAPI.updateTrainingSession(sess.id, patch);
      const h = new Date(patch.start_datetime).getHours();
      toast.success(`Перенесено на ${String(h).padStart(2, "0")}:00`);
      onUpdated?.();
    },
    onBlocked: (msg) => toast.error(msg),
  });

  const dayTitle = new Date(`${day}T12:00:00`).toLocaleDateString("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  return (
    <div className={`${s.calDayLayout} ${appCompact ? s.calDayLayoutApp : ""}`}>
      <div className={s.calDayTimelineWrap}>
        <header
          className={
            appCompact ? s.calDayTimelineHeadApp : s.calDayTimelineHead
          }
        >
          <div className={s.calDayTimelineHeadMain}>
            <h3 className={s.calDayTimelineTitle}>{dayTitle}</h3>
            {!appCompact ? (
              <p className={s.calDayDragHint}>
                {detailMode === "sheet"
                  ? "Нажмите на занятие — откроется карточка с перекличкой и действиями."
                  : "Перетащите занятие на другой час до его начала. На прошедшее время и после старта тренировки перенос недоступен."}
              </p>
            ) : null}
          </div>
          <span className={s.calDayTimelineCount}>
            {daySessions.length}{" "}
            {daySessions.length === 1 ? "занятие" : "занятий"}
          </span>
        </header>
        {dnd.dropHint ? (
          <div className={s.calDragHintBanner} role="status" aria-live="polite">
            {dnd.dropHint}
          </div>
        ) : null}
        <div
          className={s.calDayTimeline}
          onDragEnd={appCompact ? undefined : dnd.onDragEnd}
        >
          {CALENDAR_HOURS.map((hour) => {
            const cellSessions = sessionsStartingInHour(daySessions, hour);
            const isNow =
              new Date().getHours() === hour &&
              day === new Date().toISOString().slice(0, 10);
            const cell = { day, hour };
            const isDropTarget = !appCompact && dnd.isDropTarget(cell);
            return (
              <div
                key={hour}
                className={`${s.calHourRow} ${isNow ? s.calHourRowNow : ""}`}
              >
                <div className={s.calHourLabel}>
                  {String(hour).padStart(2, "0")}:00
                </div>
                <div
                  className={`${s.calHourSlot} ${isDropTarget ? s.calHourSlotDrop : ""}`}
                  onDragOver={appCompact ? undefined : (e) => dnd.overCell(e, cell)}
                  onDrop={appCompact ? undefined : (e) => dnd.dropOnCell(e, cell)}
                >
                  {cellSessions.length === 0 && isDropTarget ? (
                    <div className={s.calHourDropPlaceholder}>
                      Перенести сюда
                    </div>
                  ) : null}
                  {cellSessions.map((sess) => {
                    const groupIdx = groups.findIndex(
                      (g) => g.id === sess.group?.id,
                    );
                    const color = sessionColor(sess, groupIdx);
                    const active = selectedId === sess.id;
                    const dragging = dnd.draggingId === sess.id;
                    const rows = sessionDurationRows(sess);
                    const canDrag = !appCompact && dnd.canDragSession(sess);
                    const draggable =
                      !appCompact &&
                      sess.status !== "cancelled" &&
                      sess.status !== "completed";
                    return (
                      <div
                        key={sess.id}
                        draggable={draggable}
                        title={appCompact ? sessionLabel(sess) : dnd.cardTitle(sess)}
                        className={`${s.calHourCardWrap} ${active ? s.calHourCardWrapActive : ""} ${dragging ? s.calHourCardDragging : ""} ${!canDrag && !appCompact ? s.calHourCardLocked : ""}`}
                        style={{ minHeight: `${Math.max(rows * 56, 56)}px` }}
                        onDragStart={
                          appCompact ? undefined : (e) => dnd.startDrag(e, sess)
                        }
                        onDrag={appCompact ? undefined : dnd.onDrag}
                        onClickCapture={
                          appCompact ? undefined : dnd.onClickCapture
                        }
                      >
                        <SessionCalendarCard
                          sess={sess}
                          color={color}
                          variant="timeline"
                          active={active}
                          onClick={() => pickSession(sess.id)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {detailMode === "aside" ? (
        <aside className={s.calDayAside}>
          <DaySessionPanel
            sessionId={selectedId}
            sessionPreview={selectedPreview}
            groups={groups}
            branches={branches}
            doctors={doctors}
            onUpdated={onUpdated}
          />
        </aside>
      ) : null}
    </div>
  );
}
